import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { MessageCircle, X, Send, Sparkles, Loader2, User, Bot } from 'lucide-react';
import { useAppMode } from '../../contexts/AppModeContext';

const PATIENT_DATA = {
  P001: { name: 'John Smith', age: 60, gender: 'Male', mrn: 'MRN-001', problems: 'Type 2 DM (E11.9), Essential HTN (I10), Hyperlipidemia (E78.5)', meds: 'Metformin 500mg BID, Lisinopril 10mg daily, Atorvastatin 20mg QHS', allergies: 'Penicillin (rash), Sulfa (hives)', lastVisit: 'Dec 10, 2025', hba1c: '7.2%', bp: '138/82' },
  P002: { name: 'Maria Garcia', age: 47, gender: 'Female', mrn: 'MRN-002', problems: 'Asthma (J45.20), GERD (K21.0)', meds: 'Albuterol PRN, Fluticasone 220mcg BID, Omeprazole 20mg daily', allergies: 'NKDA', lastVisit: 'Jan 15, 2026', hba1c: 'N/A', bp: '122/78' },
  P003: { name: 'Robert Johnson', age: 45, gender: 'Male', mrn: 'MRN-003', problems: 'Chronic Low Back Pain (M54.5), Anxiety (F41.1)', meds: 'Gabapentin 300mg TID, Sertraline 50mg daily, Naproxen 500mg PRN', allergies: 'Codeine (nausea)', lastVisit: 'Jan 28, 2026', hba1c: 'N/A', bp: '126/80' },
  P004: { name: 'Emily Davis', age: 55, gender: 'Female', mrn: 'MRN-004', problems: 'HTN (I10), Hypothyroidism (E03.9), Osteoarthritis (M19.90)', meds: 'Amlodipine 5mg daily, Levothyroxine 75mcg daily, Acetaminophen PRN', allergies: 'Latex (contact dermatitis)', lastVisit: 'Feb 1, 2026', hba1c: 'N/A', bp: '142/88' },
  P005: { name: 'Sarah Williams', age: 38, gender: 'Female', mrn: 'MRN-005', problems: 'Migraine (G43.909), Depression (F32.1)', meds: 'Sumatriptan 50mg PRN, Topiramate 25mg daily, Escitalopram 10mg daily', allergies: 'Aspirin (angioedema)', lastVisit: 'Jan 20, 2026', hba1c: 'N/A', bp: '118/72' },
};

const DEMO_GENERIC_PROMPTS = [
  'What labs are overdue for my diabetic patients?',
  'Draft a follow-up note for today\'s visits',
  'Which patients need HbA1c testing?',
  'Show me my schedule for today',
];

const DEMO_PATIENT_PROMPTS = (p) => [
  `Summarize ${p.name.split(' ')[0]}'s chart`,
  `What's overdue for ${p.name.split(' ')[0]}?`,
  `Any medication alerts?`,
  `Suggest next steps`,
];

const DEMO_GENERIC_RESPONSE = (msg) => {
  const lower = msg.toLowerCase();
  if (lower.includes('lab') || lower.includes('overdue'))
    return "I found **27 patients** with overdue HbA1c tests and **11 patients** overdue for diabetic eye exams.\n\nTop priority:\n1. James Taylor (71y) — 303 days overdue, A1c 9.8%\n2. David Wilson (68y) — 281 days overdue, A1c 10.1%\n3. John Smith (60y) — 245 days overdue\n\nWould you like me to generate outreach reminders?";
  if (lower.includes('follow') || lower.includes('note') || lower.includes('draft'))
    return "Today you saw **4 patients**:\n\n1. **John Smith** — DM follow-up, in progress\n2. **Maria Garcia** — New patient, pending note\n3. **Robert Johnson** — Sick visit, pending note\n4. **Emily Davis** — HTN follow-up, pending note\n\nWould you like me to draft follow-up notes?";
  if (lower.includes('hba1c') || lower.includes('a1c') || lower.includes('diabetic'))
    return "**27 patients** need HbA1c testing across your panel of 120 diabetic patients.\n\n- 5 patients are **high risk** (overdue > 250 days)\n- 8 patients have **uncontrolled A1c > 9%**\n\nView the full list in **MIPS Dashboard → Population Health**.";
  if (lower.includes('schedule') || lower.includes('today'))
    return "**Today's Schedule (4 patients):**\n\n9:00 AM — John Smith (Follow-up)\n9:30 AM — Maria Garcia (New Patient)\n10:00 AM — Robert Johnson (Sick Visit)\n10:30 AM — Emily Davis (Follow-up)\n\nAll rooms assigned. No cancellations.";
  return "I can help with patient lookups, clinical summaries, documentation, MIPS tracking, and scheduling. Try asking about a specific patient or task.";
};

const DEMO_PATIENT_RESPONSE = (msg, p) => {
  const lower = msg.toLowerCase();
  if (lower.includes('summar') || lower.includes('chart') || lower.includes('overview'))
    return `**${p.name}** (${p.age}y, ${p.gender}, ${p.mrn})\n\n**Active Problems:**\n${p.problems.split(', ').map(pr => `- ${pr}`).join('\n')}\n\n**Medications:**\n${p.meds.split(', ').map(m => `- ${m}`).join('\n')}\n\n**Allergies:** ${p.allergies}\n**Last Visit:** ${p.lastVisit}${p.hba1c !== 'N/A' ? `\n**HbA1c:** ${p.hba1c}` : ''}\n**BP:** ${p.bp}`;
  if (lower.includes('overdue') || lower.includes('due') || lower.includes('gap'))
    return `**Care gaps for ${p.name}:**\n\n${p.hba1c !== 'N/A' ? `- HbA1c recheck overdue (last: ${p.hba1c}, ${p.lastVisit})\n- Diabetic eye exam due\n- Diabetic foot exam due\n- Lipid panel recommended` : `- Annual wellness visit recommended\n- Preventive screenings per age/sex guidelines`}\n- Flu vaccine not on record\n\nWould you like me to schedule these or generate outreach?`;
  if (lower.includes('med') || lower.includes('drug') || lower.includes('interaction') || lower.includes('alert'))
    return `**Medication Review for ${p.name}:**\n\n${p.meds.split(', ').map(m => `- ${m}`).join('\n')}\n\n**Alerts:**\n${p.problems.includes('DM') ? '- Metformin: Hold 48h before contrast procedures\n- Atorvastatin: Annual LFT recommended\n' : '- No critical interactions detected\n'}- Adherence Score: ${p.problems.includes('DM') ? '87%' : '92%'}\n\nAllergies: ${p.allergies}`;
  if (lower.includes('next') || lower.includes('suggest') || lower.includes('recommend') || lower.includes('plan'))
    return `**Suggested next steps for ${p.name}:**\n\n1. ${p.hba1c !== 'N/A' ? `Recheck HbA1c (last ${p.hba1c})` : 'Order routine labs per age guidelines'}\n2. Update problem list if new findings\n3. Medication reconciliation — verify adherence\n4. ${p.bp > '135' ? 'BP above target — consider adjusting antihypertensive' : 'Continue current management'}\n5. Schedule follow-up in ${p.problems.includes('DM') ? '3' : '6'} months\n\nWould you like me to draft orders for these?`;
  return `I'm viewing **${p.name}'s chart**. I can help with:\n- Chart summary\n- Care gaps & overdue items\n- Medication review & alerts\n- Suggested next steps\n\nWhat would you like to know?`;
};

const LuminaChatbot = () => {
  const { isDemo } = useAppMode();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const prevPatientRef = useRef(null);

  // Detect patient context from URL
  const patientMatch = location.pathname.match(/\/lumina\/patients\/(P\d+)/);
  const patientId = patientMatch ? patientMatch[1] : null;
  const patient = patientId ? PATIENT_DATA[patientId] : null;

  // Reset messages when patient changes
  useEffect(() => {
    if (prevPatientRef.current !== patientId) {
      prevPatientRef.current = patientId;
      if (patient) {
        setMessages([{ role: 'assistant', content: `I'm now viewing **${patient.name}'s chart** (${patient.age}y, ${patient.mrn}). Ask me to summarize, check care gaps, review medications, or suggest next steps.` }]);
      } else {
        setMessages([{ role: 'assistant', content: isDemo
          ? 'Hi Dr. Chen, I\'m your AI clinical assistant. Ask me anything about your patients, schedule, or documentation.'
          : 'Hi, I\'m your AI clinical assistant. Ask me anything about your patients, schedule, or documentation.' }]);
      }
    }
  }, [patientId, patient, isDemo]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = (text) => {
    const msg = text || input;
    if (!msg.trim()) return;
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setIsTyping(true);
    setTimeout(() => {
      // Real mode: route to a neutral fallback until a backend chatbot
      // service is wired. NEVER return demo canned responses with
      // fabricated patient names in real mode.
      const response = !isDemo
        ? "I'm currently a demo-only assistant. Live patient lookups will be available once the chatbot service is connected."
        : (patient ? DEMO_PATIENT_RESPONSE(msg, patient) : DEMO_GENERIC_RESPONSE(msg));
      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
      setIsTyping(false);
    }, 700 + Math.random() * 500);
  };

  const prompts = !isDemo
    ? ['How does Lumina work?', 'How do I open a chart?', 'What is MIPS?']
    : (patient ? DEMO_PATIENT_PROMPTS(patient) : DEMO_GENERIC_PROMPTS);

  return (
    <>
      {!isOpen && (
        <button onClick={() => setIsOpen(true)} className="fixed bottom-[70px] right-6 z-[9999] h-14 w-14 rounded-full bg-black text-white shadow-lg hover:bg-gray-800 flex items-center justify-center transition-all hover:scale-105" data-testid="chatbot-toggle">
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {isOpen && (
        <div className="fixed bottom-[70px] right-6 z-[9999] w-[400px] h-[560px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="chatbot-window">
          <div className="bg-black px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-white/10 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Lumina AI</p>
                <p className="text-[10px] text-slate-400">{patient ? `Viewing ${patient.name}` : 'Clinical assistant'}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/10" onClick={() => setIsOpen(false)} data-testid="chatbot-close">
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={cn("flex gap-2", msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                {msg.role === 'assistant' && (
                  <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="h-3.5 w-3.5 text-slate-600" />
                  </div>
                )}
                <div className={cn("max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed", msg.role === 'user' ? "bg-black text-white rounded-br-md" : "bg-slate-100 text-slate-800 rounded-bl-md")} data-testid={`chat-message-${i}`}>
                  {msg.content.split('\n').map((line, j) => (
                    <p key={j} className={j > 0 ? 'mt-1' : ''}>
                      {line.split(/(\*\*.*?\*\*)/).map((part, k) =>
                        part.startsWith('**') && part.endsWith('**') ? <strong key={k}>{part.slice(2, -2)}</strong> : part
                      )}
                    </p>
                  ))}
                </div>
                {msg.role === 'user' && (
                  <div className="h-7 w-7 rounded-full bg-slate-800 flex items-center justify-center shrink-0 mt-0.5">
                    <User className="h-3.5 w-3.5 text-white" />
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="flex gap-2">
                <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><Bot className="h-3.5 w-3.5 text-slate-600" /></div>
                <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 text-slate-500 animate-spin" /><span className="text-xs text-slate-500">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {messages.length <= 2 && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {prompts.map((prompt, i) => (
                <button key={i} onClick={() => handleSend(prompt)} className="text-[11px] px-2.5 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors" data-testid={`chat-suggestion-${i}`}>
                  {prompt}
                </button>
              ))}
            </div>
          )}

          <div className="border-t p-3 flex gap-2 shrink-0">
            <Input placeholder={patient ? `Ask about ${patient.name.split(' ')[0]}...` : "Ask about patients, labs, notes..."} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend()} className="flex-1 text-sm" data-testid="chatbot-input" />
            <Button size="icon" className="bg-black hover:bg-gray-800 h-9 w-9 shrink-0" onClick={() => handleSend()} disabled={!input.trim()} data-testid="chatbot-send">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default LuminaChatbot;
